import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  ArrowRightLeft,
  Send,
  CheckCircle,
  Clock,
  Eye,
  PackageCheck,
  AlertCircle,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { api } from '../lib/api';

// Lifecycle states from server/orders.js. `delivered` and `returned` are the
// same HP scan named for the direction the parcel was travelling.
const LABEL_STATUS_BADGES = {
  label_created: { cls: 'badge-green', Icon: CheckCircle, text: 'Label created' },
  in_transit: { cls: 'badge-blue', Icon: PackageCheck, text: 'In transit (HP)' },
  delivered: { cls: 'badge-green', Icon: PackageCheck, text: 'Delivered' },
  returned: { cls: 'badge-green', Icon: PackageCheck, text: 'Returned' },
  return_requested: { cls: 'badge-yellow', Icon: Clock, text: 'Return requested' },
};

/**
 * Orders come from the Shopify Admin API one page at a time. Search and status
 * filtering are pushed to Shopify's own order query rather than filtering an
 * array in the browser -- a real store has far more than one page of orders.
 */
export default function OrdersList({ reloadKey, onCreateLabel, onViewLabel }) {
  const [orders, setOrders] = useState([]);
  const [pageInfo, setPageInfo] = useState({ hasNextPage: false, endCursor: null });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Debounce typing so each keystroke does not become an Admin API call.
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const load = useCallback(
    async ({ cursor = null } = {}) => {
      if (cursor) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      try {
        const data = await api.getOrders({ search, filter: statusFilter, cursor, limit: 50 });
        setOrders((prev) => (cursor ? [...prev, ...data.orders] : data.orders));
        setPageInfo(data.pageInfo || { hasNextPage: false, endCursor: null });
      } catch (err) {
        setError(err.message);
        if (!cursor) setOrders([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [search, statusFilter]
  );

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  // One row per direction inside the "HP labels" cell, so an order that has both
  // an outbound and a return label shows both rather than only the newer one.
  const renderLabelRow = (prefix, status, label, emptyText) => {
    const badge = LABEL_STATUS_BADGES[status];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.72rem', color: 'var(--color-secondary)', minWidth: '3.1rem' }}>
          {prefix}
        </span>
        {badge ? (
          <span className={`badge ${badge.cls}`}>
            <badge.Icon size={13} /> {badge.text}
          </span>
        ) : (
          <span className="badge badge-gray">{emptyText}</span>
        )}
        {label?.barcode && (
          <span
            style={{
              fontSize: '0.72rem',
              color: 'var(--color-brand-hp)',
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          >
            {label.barcode}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="polaris-card">
      <div className="card-header" style={{ flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 className="card-title">
            Shopify orders{orders.length ? ` (${orders.length}${pageInfo.hasNextPage ? '+' : ''})` : ''}
          </h2>
          <p style={{ fontSize: '0.83rem', color: 'var(--color-secondary)', marginTop: '0.15rem' }}>
            Create a Hrvatska Pošta shipping label to send an order, or a return label for a customer to send it back.
          </p>
        </div>

        <button className="btn btn-secondary btn-sm" onClick={() => load()} disabled={loading}>
          {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>

      <div
        className="toolbar-row"
        style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid var(--color-border-subtle)',
          background: '#fafafa',
        }}
      >
        <div className="toolbar-search">
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-secondary)',
            }}
          />
          <input
            type="text"
            className="form-control"
            style={{ paddingLeft: '2.3rem' }}
            placeholder="Search by order number, customer name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="toolbar-actions">
          {[
            ['all', 'All'],
            ['return_requested', 'Return requested'],
            ['fulfilled', 'Fulfilled'],
          ].map(([value, label]) => (
            <button
              key={value}
              className={`btn btn-sm ${statusFilter === value ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setStatusFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div
          style={{
            margin: '1rem 1.5rem 0',
            background: '#fce8e6',
            color: '#c5221f',
            padding: '0.75rem 1rem',
            borderRadius: '6px',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="table-responsive" role="region" aria-label="Orders" tabIndex={0}>
        <table className="polaris-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Total</th>
              <th>Fulfillment</th>
              <th>HP labels</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="table-state">
                  <div className="table-state-inner">
                    <Loader2 size={28} className="spin" style={{ marginBottom: '0.5rem' }} />
                    <p>Loading orders from Shopify...</p>
                  </div>
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order) => (
                <tr key={order.id}>
                  <td data-label="Order" className="table-primary-cell">
                    <strong style={{ color: 'var(--color-primary)' }}>{order.name}</strong>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-secondary)' }}>
                      {order.lineItems.length} {order.lineItems.length === 1 ? 'item' : 'items'}
                    </div>
                  </td>
                  <td data-label="Date">
                    <div style={{ fontSize: '0.85rem' }}>
                      {new Date(order.createdAt).toLocaleDateString('hr-HR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </div>
                  </td>
                  <td data-label="Customer">
                    <div>
                      <div style={{ fontWeight: 500 }}>{order.customer?.name}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--color-secondary)' }}>
                        {order.shippingAddress
                          ? `${order.shippingAddress.city || '-'}, ${order.shippingAddress.countryCode || '-'}`
                          : 'No shipping address'}
                      </div>
                    </div>
                  </td>
                  <td data-label="Total">
                    <strong style={{ fontSize: '0.9rem' }}>
                      {new Intl.NumberFormat('hr-HR', {
                        style: 'currency',
                        currency: order.currency || 'EUR',
                      }).format(parseFloat(order.totalPrice))}
                    </strong>
                  </td>
                  <td data-label="Fulfillment">
                    <span className={`badge ${order.fulfillmentStatus === 'fulfilled' ? 'badge-green' : 'badge-gray'}`}>
                      {order.fulfillmentStatus}
                    </span>
                  </td>
                  <td data-label="HP labels">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      {renderLabelRow('Ship', order.shippingStatus, order.shippingLabel, 'Not shipped')}
                      {renderLabelRow('Return', order.returnStatus, order.returnLabel, 'No return')}
                    </div>
                  </td>
                  <td className="table-actions" style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.35rem',
                        alignItems: 'flex-end',
                      }}
                    >
                      {order.shippingLabel ? (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => onViewLabel(order.shippingLabel, order.name)}
                        >
                          <Eye size={14} /> View shipping label
                        </button>
                      ) : (
                        <button
                          className="btn btn-hp btn-sm"
                          onClick={() => onCreateLabel(order, 'OUTBOUND')}
                        >
                          <Send size={14} /> Ship to customer
                        </button>
                      )}

                      {order.returnLabel ? (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => onViewLabel(order.returnLabel, order.name)}
                        >
                          <Eye size={14} /> View return label
                        </button>
                      ) : (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => onCreateLabel(order, 'RETURN')}
                        >
                          <ArrowRightLeft size={14} /> Return label
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="table-state">
                  <div className="table-state-inner">
                    <AlertCircle size={32} style={{ opacity: 0.4, marginBottom: '0.5rem' }} />
                    <p>No orders found.</p>
                    <p style={{ fontSize: '0.8rem', marginTop: '0.35rem' }}>
                      Note: without the <code>read_all_orders</code> scope Shopify only exposes the
                      last 60 days of orders.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pageInfo.hasNextPage && !loading && (
        <div style={{ padding: '1rem 1.5rem', textAlign: 'center', borderTop: '1px solid var(--color-border-subtle)' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => load({ cursor: pageInfo.endCursor })}
            disabled={loadingMore}
          >
            {loadingMore ? <Loader2 size={14} className="spin" /> : null} Load more orders
          </button>
        </div>
      )}
    </div>
  );
}
